import _clamp from 'lodash/clamp';
import { HANDLE_POSITIONS } from '@src/components/wysiwyg_editor/page_content_items/DragHandle';
import type { TRect } from '@src/types/misc';
import type { SnappableLocations } from '../../hooks/wysiwyg_editor/useSnappableLocations';

type TMinimumDimensions = {
  height: number;
  width: number;
};
export const MINIMUM_DIMENSION = 10;
export const SNAP_DISTANCE = 2;

function handleElementMovementFromLeft(
  initialDragLocation: TRect,
  movement: [number, number],
  renderableArea: TRect,
  minimumDimensions: TMinimumDimensions,
  snappableLocations: Array<number>,
  snapDistance: number
) {
  const intendedPosition = initialDragLocation.x + movement[0];
  const snappedPosition = snapToPositions(
    intendedPosition,
    snappableLocations,
    snapDistance
  )[0];

  const newX = _clamp(
    snappedPosition,
    renderableArea.x,
    initialDragLocation.x + initialDragLocation.width - minimumDimensions.width
  );

  const newWidth = initialDragLocation.x + initialDragLocation.width - newX;

  return {
    x: newX,
    width: newWidth
  };
}

function handleElementMovementFromRight(
  initialDragLocation: TRect,
  movement: [number, number],
  renderableArea: TRect,
  minimumDimensions: TMinimumDimensions,
  snappableLocations: Array<number>,
  snapDistance: number
) {
  const minRight = minimumDimensions.width;
  const maxRight =
    renderableArea.x + renderableArea.width - initialDragLocation.x;
  const intendedRight =
    initialDragLocation.x + initialDragLocation.width + movement[0];
  const snappedRight = snapToPositions(
    intendedRight,
    snappableLocations,
    snapDistance
  )[0];
  const snappedWidth = snappedRight - initialDragLocation.x;
  const newWidth = _clamp(snappedWidth, minRight, maxRight);

  return {
    width: newWidth
  };
}

function snapToPositions(
  position: number,
  positions: Array<number>,
  snapDistance: number
): [number, boolean] {
  const firstSnappable = positions.find((possibility) => {
    return Math.abs(position - possibility) <= snapDistance;
  });

  if (typeof firstSnappable === 'number') return [firstSnappable, true];

  return [position, false];
}

function handleElementMovementFromTop(
  initialDragLocation: TRect,
  movement: [number, number],
  renderableArea: TRect,
  minimumDimensions: TMinimumDimensions,
  snappableLocations: Array<number>,
  snapDistance: number
) {
  const intendedPosition = initialDragLocation.y + movement[1];
  const snappedPosition = snapToPositions(
    intendedPosition,
    snappableLocations,
    snapDistance
  )[0];
  const newY = _clamp(
    snappedPosition,
    renderableArea.y,
    initialDragLocation.y +
      initialDragLocation.height -
      minimumDimensions.height
  );

  const newHeight = initialDragLocation.y + initialDragLocation.height - newY;

  return {
    y: newY,
    height: newHeight
  };
}

function handleElementMovementFromBottom(
  initialDragLocation: TRect,
  movement: [number, number],
  renderableArea: TRect,
  minimumDimensions: TMinimumDimensions,
  snappableLocations: Array<number>,
  snapDistance: number
) {
  const minBottom = minimumDimensions.height;
  const maxBottom =
    renderableArea.y + renderableArea.height - initialDragLocation.y;
  const intendedBottom =
    initialDragLocation.y + initialDragLocation.height + movement[1];
  const snappedBottom = snapToPositions(
    intendedBottom,
    snappableLocations,
    snapDistance
  )[0];
  const snappedHeight = snappedBottom - initialDragLocation.y;
  const newHeight = _clamp(snappedHeight, minBottom, maxBottom);

  return {
    height: newHeight
  };
}

function handleElementMovementFromInner(
  initialDragLocation: TRect,
  movement: [number, number],
  renderableArea: TRect,
  snappableLocations: SnappableLocations,
  snapDistance: {
    x: number;
    y: number;
  }
) {
  const intendedNewXLeft = initialDragLocation.x + movement[0];
  const intendedNewXCenter = intendedNewXLeft + initialDragLocation.width / 2;
  const intendedNewXRight = intendedNewXLeft + initialDragLocation.width;
  const intendedNewYTop = initialDragLocation.y + movement[1];
  const intendedNewYMiddle = intendedNewYTop + initialDragLocation.height / 2;
  const intendedNewYBottom = intendedNewYTop + initialDragLocation.height;

  const xKeys = Object.keys(snappableLocations.x).map(parseFloat);
  const yKeys = Object.keys(snappableLocations.y).map(parseFloat);

  const snappedXLeft = [
    [intendedNewXLeft, (val: number) => val],
    [intendedNewXCenter, (val: number) => val - initialDragLocation.width / 2],
    [intendedNewXRight, (val: number) => val - initialDragLocation.width]
  ].reduce((a: null | number, e: [number, (val: number) => number]) => {
    if (a !== null) return a;

    const [snappablePosition, snappedPositionConversion] = e;

    const snapped = snapToPositions(snappablePosition, xKeys, snapDistance.x);

    if (snapped[1]) {
      return snappedPositionConversion(snapped[0]);
    } else {
      return null;
    }
  }, null);

  const snappedYTop = [
    [intendedNewYTop, (val: number) => val],
    [intendedNewYMiddle, (val: number) => val - initialDragLocation.height / 2],
    [intendedNewYBottom, (val: number) => val - initialDragLocation.height]
  ].reduce((a: null | number, e: [number, (val: number) => number]) => {
    if (a !== null) return a;

    const [snappablePosition, snappedPositionConversion] = e;

    const snapped = snapToPositions(snappablePosition, yKeys, snapDistance.y);

    if (snapped[1]) {
      return snappedPositionConversion(snapped[0]);
    } else {
      return null;
    }
  }, null);

  const newX = _clamp(
    snappedXLeft || intendedNewXLeft,
    renderableArea.x,
    renderableArea.x + renderableArea.width - initialDragLocation.width
  );

  const newY = _clamp(
    snappedYTop || intendedNewYTop,
    renderableArea.y,
    renderableArea.y + renderableArea.height - initialDragLocation.height
  );

  return {
    x: newX,
    y: newY
  };
}

export function handleElementMovement(
  position: string,
  initialDragLocation: TRect,
  movement: [number, number],
  renderableArea: TRect,
  minimumDimensions: TMinimumDimensions,
  snappableLocations: SnappableLocations,
  snappableDistances: { x: number; y: number }
): TRect {
  let newLocation = { ...initialDragLocation };

  if (
    [HANDLE_POSITIONS.n, HANDLE_POSITIONS.ne, HANDLE_POSITIONS.nw].indexOf(
      position
    ) >= 0
  ) {
    newLocation = {
      ...newLocation,
      ...handleElementMovementFromTop(
        initialDragLocation,
        movement,
        renderableArea,
        minimumDimensions,
        Object.keys(snappableLocations.y).map(parseFloat),
        snappableDistances.y
      )
    };
  }

  if (
    [HANDLE_POSITIONS.s, HANDLE_POSITIONS.se, HANDLE_POSITIONS.sw].indexOf(
      position
    ) >= 0
  ) {
    newLocation = {
      ...newLocation,
      ...handleElementMovementFromBottom(
        initialDragLocation,
        movement,
        renderableArea,
        minimumDimensions,
        Object.keys(snappableLocations.y).map(parseFloat),
        snappableDistances.y
      )
    };
  }
  if (
    [HANDLE_POSITIONS.w, HANDLE_POSITIONS.nw, HANDLE_POSITIONS.sw].indexOf(
      position
    ) >= 0
  ) {
    newLocation = {
      ...newLocation,
      ...handleElementMovementFromLeft(
        initialDragLocation,
        movement,
        renderableArea,
        minimumDimensions,
        Object.keys(snappableLocations.x).map(parseFloat),
        snappableDistances.x
      )
    };
  }

  if (
    [HANDLE_POSITIONS.e, HANDLE_POSITIONS.ne, HANDLE_POSITIONS.se].indexOf(
      position
    ) >= 0
  ) {
    newLocation = {
      ...newLocation,
      ...handleElementMovementFromRight(
        initialDragLocation,
        movement,
        renderableArea,
        minimumDimensions,
        Object.keys(snappableLocations.x).map(parseFloat),
        snappableDistances.x
      )
    };
  }

  if (position === HANDLE_POSITIONS.inner) {
    newLocation = {
      ...newLocation,
      ...handleElementMovementFromInner(
        initialDragLocation,
        movement,
        renderableArea,
        snappableLocations,
        snappableDistances
      )
    };
  }

  return newLocation;
}

export function shouldSnapToOriginalLocation(
  dragPosition: string,
  movement: [number, number],
  snapDistance: number
): boolean {
  return false;
  if ([HANDLE_POSITIONS.n, HANDLE_POSITIONS.s].includes(dragPosition)) {
    return Math.abs(movement[1]) <= snapDistance;
  } else if ([HANDLE_POSITIONS.e, HANDLE_POSITIONS.w].includes(dragPosition)) {
    return Math.abs(movement[0]) <= snapDistance;
  } else {
    return (
      Math.max(Math.abs(movement[0]), Math.abs(movement[1])) <= snapDistance
    );
  }
}
